import React, { useState, useEffect } from "react";
import { updateUser } from "./api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

interface Role {
  role_id: number;
  role_desc: string;
}

interface User {
  ecode: string;
  name: string;
  email: string;
  mobile_no: string;
  role_name: string;
  role_id: number;
  is_active: boolean;
  login_count: number;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onUserUpdated: () => void;
  roles: Role[];
}

const EditUserModal: React.FC<EditUserModalProps> = ({ 
  open, 
  onOpenChange, 
  user,
  onUserUpdated, 
  roles 
}) => {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    mobile_no: "",
    email: "",
    role_id: "",
    is_active: true
  });

  // Populate form when opened
  useEffect(() => {
    if (open && user) {
      populateForm();
    }
  }, [open, user]);

  const populateForm = () => {
    if (!user) return;
    
    setUpdateError("");
    setValidationErrors({});
    
    setFormData({
      name: user.name,
      mobile_no: user.mobile_no,
      email: user.email,
      role_id: user.role_id.toString(),
      is_active: user.is_active
    });
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    // Real-time validation
    if (field === "mobile_no" && typeof value === "string" && value) {
      const mobileRegex = /^\d{10}$/;
      if (value.length > 0 && !mobileRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          mobile_no: value.length !== 10 ? "Must be exactly 10 digits" : "Only numbers allowed"
        }));
      }
    }

    if (field === "email" && typeof value === "string" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 0 && !emailRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: "Please enter a valid email address"
        }));
      }
    }
  };


  const handleUpdateUser = async () => {
    if (!user) return;

    try {
      setUpdating(true);
      setUpdateError("");

      // Validation
      if (!formData.name.trim()) {
        setUpdateError("Name is required");
        return;
      }

      // Mobile number validation (10 digits)
      if (!formData.mobile_no.trim()) {
        setUpdateError("Mobile number is required");
        return;
      }
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile_no.trim())) {
        setUpdateError("Mobile number must be exactly 10 digits");
        return;
      }

      // Email validation
      if (!formData.email.trim()) {
        setUpdateError("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setUpdateError("Please enter a valid email address");
        return;
      }

      if (!formData.role_id) {
        setUpdateError("Role is required");
        return;
      }

      const userData = {
        name: formData.name.trim(),
        mobile_no: formData.mobile_no.trim(),
        email: formData.email.trim(),
        role_id: parseInt(formData.role_id)
      };

      await updateUser(user.ecode, userData);

      // Notify parent component to refresh users list
      if (onUserUpdated) {
        onUserUpdated();
      }

      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error updating user:", err);

      // Extract error message from backend response
      let errorMessage = "Failed to update user";
      
      if (err?.response?.data?.detail) {
        // Handle string detail message
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
        // Handle array of validation errors
        else if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
          const firstError = err.response.data.detail[0];
          errorMessage = firstError.msg || "Validation error";
        }
      } else if (err?.response?.status === 400 && err?.response?.data) {
        // Handle other 400 error formats
        errorMessage = err.response.data.detail || err.response.data.message || "Bad request";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setUpdateError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Edit User</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {updateError && (
            <Alert variant="destructive">
              <AlertDescription>{updateError}</AlertDescription>
            </Alert>
          )}


          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
            />
          </div>

          {/* Mobile Number */}
          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number *</Label>
            <Input
              id="mobile"
              value={formData.mobile_no}
              onChange={(e) => handleInputChange('mobile_no', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className={validationErrors.mobile_no ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.mobile_no && (
              <p className="text-sm text-red-600">{validationErrors.mobile_no}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium">Role *</Label>
            <Select value={formData.role_id} onValueChange={(value) => handleInputChange('role_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id.toString()}>
                    {role.role_desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} disabled={updating}>
            {updating ? 'Updating...' : 'Update User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
